import { useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_, type ConfigPackVersionDetail } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { PRESETS, describeRule, keyFrom } from "../system/payRules";

// ---------------------------------------------------------------------------
// SWEEP S6. A DRAFT VERSION COULD BE READ AND NOT WRITTEN.
//
// The routes to add a role and a pay rule set existed with no caller, and nothing at
// all wrote `app.pay_rule` — the rows the V23 engine takes every rate from. A tenant
// that forked a template got pay rule sets that paid nothing and no way to say what
// anybody earns. This file is the editing half of a draft; Configuration.tsx keeps the
// reading half.
//
// THE RATES ARE OFFERED AS THE HANDFUL OF RULES AN AGENCY ACTUALLY WRITES, not as the
// engine's twelve effect kinds and ten triggers. Each preset is one row of
// `app.pay_rule` with its spec filled in; the server checks the spec keys (config.ts
// EFFECT_KEYS), and the bands and ordering are its business.
// ---------------------------------------------------------------------------

type Failable<T> = T & { error?: { message?: string } };

function useAct(onChanged: () => Promise<void>) {
  const { t, refusal } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function act(request: Parameters<typeof send>[0], ok: number[], fail: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const res = await send<Failable<object>>(request);
      if (!ok.includes(res.status)) {
        setError(refusal(res.body.error) ?? fail);
        return false;
      }
      await onChanged();
      return true;
    } catch {
      setError(t("We could not reach Verbunk. Check your connection and try again."));
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, setError, act };
}

function Problem({ text }: { text: string | null }) {
  const { t } = useT();
  return text ? (
    <div className="form-message" role="status" aria-live="polite">
      <StatusChip tone="failed" label={t("Not applied")} />
      <span>{text}</span>
    </div>
  ) : null;
}

/** "Add a role" — a name; the key is derived. */
export function AddRole({ versionId, onChanged }: { versionId: string; onChanged: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { t } = useT();
  const [name, setName] = useState("");
  const { busy, error, setError, act } = useAct(onChanged);
  return (
    <form
      className="row"
      onSubmit={async (e) => {
        e.preventDefault();
        if (name.trim() === "") return setError(t("Give the role a name."));
        const done = await act(
          console_.createRoleDef(apiBase, { key: keyFrom(name), name: name.trim(), config_pack_version_id: versionId }),
          [201],
          t("The role could not be added."),
        );
        if (done) setName("");
      }}
    >
      <div className="field">
        <label htmlFor={`role-${versionId}`}>{t("Add a role")}</label>
        <input id={`role-${versionId}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Bar supervisor" />
      </div>
      <button type="submit" className="btn" disabled={busy}>
        {busy ? t("Adding…") : t("Add role")}
      </button>
      <Problem text={error} />
    </form>
  );
}

/** "Add a pay rule set" — a name; its rates are added below it. */
export function AddPayRuleSet({ versionId, onChanged }: { versionId: string; onChanged: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { locale, t } = useT();
  const [name, setName] = useState("");
  // S10. WHICH SIDE OF THE MONEY: the vocabulary is read, never spelled (absolute rule 4).
  const [bases, setBases] = useState<{ id: string; label: string }[]>([]);
  const [basis, setBasis] = useState("");
  useEffect(() => {
    send<{ calculation_bases?: { id: string; label: string }[] }>(console_.calculationBases(apiBase))
      .then((res) => {
        const list = res.body.calculation_bases ?? [];
        setBases(list);
        if (list[0]) setBasis(list[0].id);
      })
      .catch(() => setBases([]));
  }, [apiBase]);
  const { busy, error, setError, act } = useAct(onChanged);
  return (
    <form
      className="row"
      onSubmit={async (e) => {
        e.preventDefault();
        if (name.trim() === "") return setError(t("Give the pay rule set a name."));
        const done = await act(
          console_.createPayRuleSet(apiBase, versionId, {
            key: keyFrom(name),
            name: name.trim(),
            rules: {},
            ...(basis ? { basis } : {}),
          }),
          [201],
          t("The pay rule set could not be added."),
        );
        if (done) setName("");
      }}
    >
      <div className="field">
        <label htmlFor={`prs-${versionId}`}>{t("Add a pay rule set")}</label>
        <input id={`prs-${versionId}`} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Weekday rates")} />
      </div>
      {bases.length > 1 ? (
        <div className="field">
          <label htmlFor={`prs-basis-${versionId}`}>{t("It prices")}</label>
          <select id={`prs-basis-${versionId}`} value={basis} onChange={(e) => setBasis(e.target.value)}>
            {bases.map((b) => (
              <option key={b.id} value={b.id}>
                {translateLabel(locale, b.label)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <button type="submit" className="btn" disabled={busy}>
        {busy ? t("Adding…") : t("Add pay rule set")}
      </button>
      <Problem text={error} />
    </form>
  );
}

/** The rates in one pay rule set: read back in words, removable and addable in a draft. */
export function PayRuleSetRates({
  set,
  editable,
  onChanged,
}: {
  set: ConfigPackVersionDetail["pay_rule_sets"][number];
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const { apiBase, me } = useSession();
  const { locale, t } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const zone = me?.authenticated ? (me.active_org?.timezone ?? "UTC") : "UTC";
  // Rates are typed and read back in the organisation's currency (S20 sets it).
  const currency = me?.authenticated ? (me.active_org?.currency ?? "GBP") : "GBP";
  const [preset, setPreset] = useState<string>("");
  const [values, setValues] = useState<Record<string, string>>({});
  const { busy, error, setError, act } = useAct(onChanged);
  const chosen = PRESETS.find((p) => p.id === preset) ?? null;
  const hasRate = set.pay_rules.some((r) => r.sets_base_rate);

  return (
    <div className="stack">
      <div className="row">
        <strong>{set.name}</strong>
        <span className="muted">{set.basis === "charge" ? t("What the client is charged") : t("What the worker is paid")}</span>
        {hasRate ? null : <StatusChip tone="attention" label={t("No hourly rate — pays nothing")} />}
      </div>
      {set.pay_rules.length === 0 ? (
        <p className="muted">{t("No rates yet.")}</p>
      ) : (
        <ul className="plain-list">
          {set.pay_rules.map((r) => (
            <li key={r.id} className="row">
              <span>
                {tl(r.label)} —{" "}
                {describeRule(
                  { ...r, effect_phrase: tl(r.effect_phrase), trigger_phrase: r.trigger_phrase ? tl(r.trigger_phrase) : r.trigger_phrase },
                  currency,
                )}
              </span>
              {editable ? (
                <button
                  type="button"
                  className="btn btn-quiet"
                  disabled={busy}
                  onClick={() => void act(console_.deletePayRule(apiBase, r.id), [200], t("That rate could not be removed."))}
                >
                  {t("Remove")}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editable ? (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!chosen) return setError(t("Choose what kind of rate to add."));
            const built = chosen.build(values, zone, currency);
            if (typeof built === "string") return setError(tl(built));
            const done = await act(
              console_.createPayRule(apiBase, set.id, { label: chosen.label, ...built }),
              [201],
              t("That rate could not be added."),
            );
            if (done) {
              setPreset("");
              setValues({});
            }
          }}
        >
          <div className="row">
            <div className="field">
              <label htmlFor={`preset-${set.id}`}>{t("Add a rate")}</label>
              <select
                id={`preset-${set.id}`}
                value={preset}
                onChange={(e) => {
                  setPreset(e.target.value);
                  setValues({});
                  setError(null);
                }}
              >
                <option value="">{t("Choose…")}</option>
                {PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {tl(p.label)}
                  </option>
                ))}
              </select>
              {chosen ? <p className="field-hint">{tl(chosen.hint)}</p> : null}
            </div>
            {chosen?.fields.map((f) => (
              <div className="field" key={f.key}>
                <label htmlFor={`${set.id}-${f.key}`}>{tl(f.label)}</label>
                <input
                  id={`${set.id}-${f.key}`}
                  value={values[f.key] ?? ""}
                  placeholder={f.placeholder}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          {chosen ? (
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t("Adding…") : t("Add {rate}", { rate: tl(chosen.label).toLowerCase() })}
            </button>
          ) : null}
          <Problem text={error} />
        </form>
      ) : null}
    </div>
  );
}

/** "Discard this draft" — with a second click, because it takes everything in it. */
export function DiscardDraft({ versionId, onChanged }: { versionId: string; onChanged: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { t } = useT();
  const [confirming, setConfirming] = useState(false);
  const { busy, error, act } = useAct(onChanged);
  return (
    <div className="row">
      {confirming ? (
        <>
          <span>{t("Discard this draft and everything in it?")}</span>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => void act(console_.discardConfigPackVersion(apiBase, versionId), [200], t("The draft could not be discarded."))}
          >
            {busy ? t("Discarding…") : t("Yes, discard it")}
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => setConfirming(false)}>
            {t("Keep it")}
          </button>
        </>
      ) : (
        <button type="button" className="btn btn-quiet" onClick={() => setConfirming(true)}>
          {t("Discard this draft")}
        </button>
      )}
      <Problem text={error} />
    </div>
  );
}
